import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { ChatInterface, type PendingMessage } from "~/components/chat";
import { ChatSkeleton } from "~/components/loading-skeleton";
import { useLocalChatSession } from "~/lib/chat/local-queries";
import { importCloudChatToLocal } from "~/lib/sync/cloud-pull";

export const Route = createFileRoute("/(authenticated)/dashboard/chat/$chatId")({
  component: ChatPage,
  pendingComponent: ChatSkeleton,
});

function ChatPage() {
  const { chatId } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = Route.useRouteContext();

  // Load chat from local IndexedDB (reactive via useLiveQuery)
  const chat = useLocalChatSession(chatId);
  const attemptedRef = useRef<string | null>(null);

  // Get pending message from cache (set by new.tsx when creating chat)
  const pendingMessage = useMemo(
    () =>
      queryClient.getQueryData<PendingMessage>(["pending-message", chatId]),
    [chatId, queryClient],
  );

  // Track if we've already processed this pending message
  const processedRef = useRef(false);

  // Clear the pending message from cache after reading it
  // Do this in useEffect to avoid side effects during render
  useEffect(() => {
    if (pendingMessage && !processedRef.current) {
      processedRef.current = true;
      queryClient.removeQueries({ queryKey: ["pending-message", chatId] });
    }
  }, [pendingMessage, chatId, queryClient]);

  useEffect(() => {
    const needsHydration =
      chat === null ||
      (chat !== undefined &&
        chat !== null &&
        chat.isSynced &&
        chat.messageCount > 0 &&
        chat.messages.length === 0);

    if (!needsHydration) return;
    if (attemptedRef.current === chatId) return;
    attemptedRef.current = chatId;

    let active = true;

    importCloudChatToLocal(chatId)
      .then((result) => {
        if (!active) return;
        if (!result.success) {
          navigate({ to: "/dashboard/chat/new" });
        }
      })
      .catch(() => {
        if (!active) return;
        navigate({ to: "/dashboard/chat/new" });
      });

    return () => {
      active = false;
    };
  }, [chat, chatId, navigate]);

  // Show loading state while chat is being fetched or hydrated
  const isPlaceholder =
    chat !== undefined &&
    chat !== null &&
    chat.isSynced &&
    chat.messageCount > 0 &&
    chat.messages.length === 0;

  if (chat === undefined || chat === null || isPlaceholder) {
    return <ChatSkeleton />;
  }

  return (
    <ChatInterface
      chatId={chatId}
      chat={chat}
      pendingMessage={pendingMessage}
      user={user}
    />
  );
}
